import React from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/atoms/Button";
import { Input } from "@/components/atoms/Input";
import { TextArea } from "@/components/atoms/TextArea";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/atoms/Select";
import { Label } from "@/components/atoms/Label";
import {
  Toast,
  ToastProvider,
  ToastViewport,
  ToastTitle,
  ToastDescription,
} from "@/components/atoms/Toast";

import type { FieldConfig } from "./types";
import FileField from "./FileField";
import LockedField from "./LockedField";
import MDXEditorField from "./MDXEditorField";
import RichTextField from "./RichTextField";
import ColorField from "./ColorField";
import RGBAField from "./RGBAField";
import IconBucketField from "./iconbucketfield";
import SearchableMultiSelectField from "./SearchableMultiSelectField";

/**
 * Coerce any stored last-modified/datetime value into the exact string an
 * <input type="datetime-local"> can display: "YYYY-MM-DDTHH:mm:ss". Handles the ISO
 * form a DB DATETIME/JSON round-trip produces ("2026-07-16T15:31:44.000Z"), the
 * space-separated SQL form ("2026-07-16 15:31:44"), and an already-clean value. Anything
 * unparseable → "" (empty input) rather than a value the control silently rejects.
 */
const toDateTimeLocalValue = (v: unknown): string => {
  const s = String(v ?? "").trim();
  if (!s) return "";
  const m = s.match(/(\d{4}-\d{2}-\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/);
  if (!m) return "";
  const [, date, hh, mm, ss] = m;
  return `${date}T${hh}:${mm}:${ss ?? "00"}`;
};

export const FieldRenderer = (
  field: FieldConfig,
  formData: Record<string, any>,
  handleChange: (fieldName: string, value: any) => void,
  handleAction: (field: FieldConfig) => Promise<void>,
  isLoading: boolean,
  toast: any,
  validationErrors: Record<string, boolean>,
  validationMessage?: string
): React.ReactNode => {
  const {
    fieldName,
    filedType,
    label,
    placeholder,
    disabled,
    className,
    required,
    valueType,
    /** Pull out so it never lands in `fieldProps` / accidental DOM spread */
    options: selectOptions,
    ...fieldProps
  } = field;
  const hasError = validationErrors[fieldName] || false;

  // For Rich Text editor configurable padding-x and padding-y when enable padding based on that x and y axis paddings will be confgiurable, Conditional visibility: skip rendering when the field this one depends on is not enabled.
  if (field.dependsOn) {
    const dep = formData[field.dependsOn];
    const depActive = dep === true || dep === 1 || dep === "1";
    if (!depActive) {
      return null;
    }
  }

  const fieldTypeMap = {
    INPUT: () => (
      <div key={fieldName} className="flex-1 min-w-80 max-w-md">
        {label && (
          <Label
            htmlFor={fieldName}
            className={`peer-disabled:cursor-not-allowed peer-disabled:opacity-70 text-sm font-medium ${
              field.labelClassName || ""
            }`}
          >
            {label}
            {field.showAsterisk !== false && required && (
              <span className="text-secondary-main ml-1">*</span>
            )}
          </Label>
        )}
        {field.description && (
          <p className="text-body-4-medium text-gray-600 mb-1 max-w-xl">
            {field.description}
          </p>
        )}
        <div className="relative mt-1">
          {field.icon && (
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-10">
              {field.icon}
            </div>
          )}
          <Input
            type={valueType === "number" ? "text" : valueType || "text"}
            inputMode={valueType === "number" ? "numeric" : undefined}
            value={formData[fieldName] ?? ""}
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
              if (valueType === "number") {
                // Allow: backspace, delete, tab, escape, enter, arrows
                const allowedKeys = [
                  "Backspace",
                  "Delete",
                  "Tab",
                  "Escape",
                  "Enter",
                  "ArrowLeft",
                  "ArrowRight",
                  "ArrowUp",
                  "ArrowDown",
                  "Home",
                  "End",
                ];
                // Allow Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
                if (
                  allowedKeys.includes(e.key) ||
                  (e.ctrlKey &&
                    ["a", "c", "v", "x"].includes(e.key.toLowerCase()))
                ) {
                  return;
                }
                // Block any non-digit key
                if (!/^\d$/.test(e.key)) {
                  e.preventDefault();
                }
              }
            }}
            onPaste={(e: React.ClipboardEvent<HTMLInputElement>) => {
              if (valueType === "number") {
                const pastedData = e.clipboardData.getData("text");
                if (!/^\d+$/.test(pastedData)) {
                  e.preventDefault();
                }
              }
            }}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const value = e.target.value;
              if (valueType === "number") {
                if (value === "" || /^\d+$/.test(value)) {
                  const numericValue = value === "" ? "" : Number(value);
                  handleChange(fieldName, numericValue);
                }
              } else {
                handleChange(fieldName, value);
              }
            }}
            placeholder={placeholder}
            disabled={disabled}
            classType="admin"
            className={`text-base md:text-sm placeholder:text-sm ${
              className || ""
            } ${
              hasError
                ? "border-secondary-main focus:border-secondary-main focus:ring-secondary-main"
                : ""
            }`}
          />
        </div>
        {hasError && (
          <p className="text-secondary-main text-body-4-medium mt-1">
            {validationMessage || "This field is required"}
          </p>
        )}
      </div>
    ),

    COLOR: () => (
      <ColorField
        fieldName={fieldName}
        label={label}
        placeholder={placeholder}
        disabled={disabled}
        className={className}
        required={required}
        hasError={hasError}
        validationMessage={validationMessage}
        formData={formData}
        handleChange={handleChange}
        field={field}
      />
    ),

    RGBA: () => (
      <RGBAField
        fieldName={fieldName}
        label={label}
        placeholder={placeholder}
        disabled={disabled}
        className={className}
        required={required}
        hasError={hasError}
        validationMessage={validationMessage}
        formData={formData}
        handleChange={handleChange}
        field={field}
      />
    ),

    TEXT_AREA: () => (
      <div
        key={fieldName}
        className={`${field.fullWidth ? "w-full" : "flex-1 min-w-80 max-w-md"} mb-4`}
      >
        {label && (
          <Label
            htmlFor={fieldName}
            className="block text-body-3-semibold font-semibold text-black mb-2"
          >
            {label}
            {required && <span className="text-secondary-main ml-1">*</span>}
          </Label>
        )}
        {field.description && (
          <p
            className={`text-body-4-medium text-gray-600 mb-2 ${field.fullWidth ? "" : "max-w-xl"}`}
          >
            {field.description}
          </p>
        )}
        <TextArea
          value={formData[fieldName] || ""}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
            handleChange(fieldName, e.target.value)
          }
          placeholder={placeholder}
          disabled={disabled}
          classType="admin"
          className={`${field.fullWidth ? "w-full " : ""}${className || ""} ${
            hasError
              ? "border-secondary-main focus:border-secondary-main focus:ring-secondary-main"
              : ""
          }`}
          rows={field.rows}
        />
        {hasError && (
          <p className="text-secondary-main text-body-4-medium mt-1">
            {validationMessage || "This field is required"}
          </p>
        )}
      </div>
    ),

    /**
     * Read-only until the admin clicks "Edit" — see LockedField. Use these instead of
     * INPUT / TEXT_AREA for settings that browser autofill must never touch and that
     * should not be editable by accident. `isSecret` fields render the stored value as
     * one asterisk per character and can be cleared to remove it.
     */
    LOCKED_INPUT: () => (
      <LockedField
        fieldName={fieldName}
        label={label}
        description={field.description}
        placeholder={placeholder}
        disabled={disabled}
        className={className}
        required={required}
        hasError={hasError}
        validationMessage={validationMessage}
        formData={formData}
        handleChange={handleChange}
        field={field}
      />
    ),

    LOCKED_TEXT_AREA: () => (
      <LockedField
        fieldName={fieldName}
        label={label}
        description={field.description}
        placeholder={placeholder}
        disabled={disabled}
        className={className}
        required={required}
        hasError={hasError}
        validationMessage={validationMessage}
        formData={formData}
        handleChange={handleChange}
        field={field}
        multiline
      />
    ),

    MDX_EDITOR: () => (
      <MDXEditorField
        fieldName={fieldName}
        label={label}
        placeholder={placeholder}
        disabled={disabled}
        className={className}
        required={required}
        hasError={hasError}
        validationMessage={validationMessage}
        formData={formData}
        handleChange={handleChange}
      />
    ),

    RICH_TEXT_EDITOR: () => (
      <RichTextField
        fieldName={fieldName}
        label={label}
        description={field.description}
        placeholder={placeholder}
        disabled={disabled}
        className={className}
        required={required}
        hasError={hasError}
        validationMessage={validationMessage}
        formData={formData}
        handleChange={handleChange}
      />
    ),

    SELECT: () => {
      const raw = formData[fieldName];
      const currentValue =
        raw != null && raw !== ""
          ? String(raw)
          : "";
      // Remount when options load (dynamic fetches) so defaultValue picks the correct item
      const optionsLen = Array.isArray(selectOptions) ? selectOptions.length : 0;
      const selectKey = `select-${fieldName}-${
        field.id || "new"
      }-${currentValue}-${optionsLen}`;

      return (
        <div key={selectKey} className="relative flex-1 min-w-80 max-w-md mb-4" style={{ isolation: 'isolate', contain: 'layout' }}>
          {label && (
            <Label
              htmlFor={fieldName}
              className="block text-body-3-semibold font-semibold text-black mb-2"
            >
              {label}
              {required && <span className="text-secondary-main ml-1">*</span>}
            </Label>
          )}
          <Select
            defaultValue={currentValue}
            onValueChange={(value) => handleChange(fieldName, value)}
            disabled={disabled}
          >
            <SelectTrigger
              classType="triggerAdmin"
              className={`${className || ""} ${
                hasError
                  ? "border-secondary-main focus:border-secondary-main focus:ring-secondary-main"
                  : ""
              }`}
              icon={field.icon}
              iconClassName={field.iconClassName}
            >
              <SelectValue placeholder={placeholder} />
            </SelectTrigger>
            <SelectContent
              classType="contentAdmin"
              viewportClassType="viewportAdmin"
            >
              {selectOptions?.map((option) => (
                <SelectItem
                  key={option.value}
                  value={option.value}
                  classType="itemAdmin"
                >
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {field.helperText && (
            <p className="text-body-4-medium text-gray-600 mt-1">
              {field.helperText}
            </p>
          )}
          {hasError && (
            <p className="text-secondary-main text-body-4-medium mt-1">
              {validationMessage || "This field is required"}
            </p>
          )}
        </div>
      );
    },

    SEARCH_MULTI_SELECT: () => (
      <SearchableMultiSelectField
        fieldName={fieldName}
        label={label}
        placeholder={placeholder}
        disabled={disabled}
        className={className}
        required={required}
        hasError={hasError}
        validationMessage={validationMessage}
        formData={formData}
        handleChange={handleChange}
        field={field}
      />
    ),

    TOGGLE: () => (
      <div key={fieldName} className="flex-1 min-w-80 max-w-md mb-4">
        {label && (
          <Label
            htmlFor={fieldName}
            className="block text-body-3-semibold font-semibold text-black mb-2"
          >
            {label}
            {required && <span className="text-secondary-main ml-1">*</span>}
          </Label>
        )}
        <label
          className={`relative inline-flex items-center cursor-pointer ${
            disabled ? "opacity-50 cursor-not-allowed" : ""
          } ${className || ""}`}
        >
          <Input
            type="checkbox"
            className="sr-only peer"
            checked={formData[fieldName] || false}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleChange(fieldName, e.target.checked)
            }
            disabled={disabled}
          />
          <div
            className={`w-11 h-6 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary-main rounded-full peer transition-colors ${
              hasError ? "bg-secondary-main" : "bg-gray-3"
            } peer-checked:bg-primary-main`}
          ></div>
          <div className="absolute left-0.5 top-0.5 w-5 h-5 bg-white rounded-full shadow-md transform transition-transform peer-checked:translate-x-5" />
        </label>
        {hasError && (
          <p className="text-secondary-main text-body-4-medium mt-1">
            {validationMessage || "This field is required"}
          </p>
        )}
      </div>
    ),

    FILE: () => (
      <FileField
        fieldName={fieldName}
        label={label}
        placeholder={placeholder}
        description={field.description}
        disabled={disabled}
        className={className}
        required={required}
        hasError={hasError}
        validationMessage={validationMessage}
        formData={formData}
        handleChange={handleChange}
        toast={toast}
        accept={field.accept}
        wrapperClassName={field.wrapperClassName}
      />
    ),

    ICON_BUCKET: () => (
      <IconBucketField
        fieldName={fieldName}
        label={label}
        placeholder={placeholder}
        disabled={disabled}
        className={className}
        required={required}
        hasError={hasError}
        validationMessage={validationMessage}
        formData={formData}
        handleChange={handleChange}
        filterCategory={field.filterCategory}
      />
    ),

    BUTTON: () => (
      <div key={fieldName} className="w-full flex justify-end">
        <Button
          type="button"
          onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            handleAction(field);
          }}
          disabled={isLoading}
          classType="admin"
          className={className}
        >
          {field.text}
        </Button>
      </div>
    ),

    TOAST: () => (
      <ToastProvider key={fieldName}>
        <Toast
          variant={
            (field.variant as "default" | "destructive" | "success") ||
            "default"
          }
          open={toast.open}
          onOpenChange={(open: boolean) =>
            toast({ title: "", description: "", variant: "default" })
          }
        >
          <ToastTitle>{field.title || ""}</ToastTitle>
          <ToastDescription>{field.description || ""}</ToastDescription>
        </Toast>
        <ToastViewport />
      </ToastProvider>
    ),

    DATE: () => (
      <div key={fieldName} className="flex-1 min-w-80 max-w-md">
        {label && (
          <Label
            htmlFor={fieldName}
            className={`peer-disabled:cursor-not-allowed peer-disabled:opacity-70 text-sm font-medium ${
              field.labelClassName || ""
            }`}
          >
            {label}
            {field.showAsterisk !== false && required && (
              <span className="text-secondary-main ml-1">*</span>
            )}
          </Label>
        )}
        {field.description && (
          <p className="text-body-4-medium text-gray-600 mb-1 max-w-xl">
            {field.description}
          </p>
        )}
        <div className="relative mt-1">
          <Input
            type="date"
            value={formData[fieldName] ?? ""}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleChange(fieldName, e.target.value)
            }
            placeholder={placeholder}
            disabled={disabled}
            classType="admin"
            className={`text-base md:text-sm placeholder:text-sm ${
              className || ""
            } ${
              hasError
                ? "border-secondary-main focus:border-secondary-main focus:ring-secondary-main"
                : ""
            }`}
          />
        </div>
        {hasError && (
          <p className="text-secondary-main text-body-4-medium mt-1">
            {validationMessage || "This field is required"}
          </p>
        )}
      </div>
    ),

    DATETIME: () => (
      <div key={fieldName} className="flex-1 min-w-80 max-w-md">
        {label && (
          <Label
            htmlFor={fieldName}
            className={`peer-disabled:cursor-not-allowed peer-disabled:opacity-70 text-sm font-medium ${
              field.labelClassName || ""
            }`}
          >
            {label}
            {field.showAsterisk !== false && required && (
              <span className="text-secondary-main ml-1">*</span>
            )}
          </Label>
        )}
        {field.description && (
          <p className="text-body-4-medium text-gray-600 mb-1 max-w-xl">
            {field.description}
          </p>
        )}
        <div className="relative mt-1">
          <Input
            type="datetime-local"
            step="1"
            value={toDateTimeLocalValue(formData[fieldName])}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleChange(fieldName, e.target.value)
            }
            placeholder={placeholder}
            disabled={disabled}
            classType="admin"
            className={`text-base md:text-sm placeholder:text-sm ${
              className || ""
            } ${
              hasError
                ? "border-secondary-main focus:border-secondary-main focus:ring-secondary-main"
                : ""
            }`}
          />
        </div>
        {hasError && (
          <p className="text-secondary-main text-body-4-medium mt-1">
            {validationMessage || "This field is required"}
          </p>
        )}
      </div>
    ),

    TIME: () => (
      <div key={fieldName} className="flex-1 min-w-80 max-w-md">
        {label && (
          <Label
            htmlFor={fieldName}
            className={`peer-disabled:cursor-not-allowed peer-disabled:opacity-70 text-sm font-medium ${
              field.labelClassName || ""
            }`}
          >
            {label}
            {field.showAsterisk !== false && required && (
              <span className="text-secondary-main ml-1">*</span>
            )}
          </Label>
        )}
        <div className="relative mt-1">
          <Input
            type="time"
            step="1"
            value={formData[fieldName] ?? ""}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleChange(fieldName, e.target.value)
            }
            placeholder={placeholder}
            disabled={disabled}
            classType="admin"
            className={`text-base md:text-sm placeholder:text-sm ${
              className || ""
            } ${
              hasError
                ? "border-secondary-main focus:border-secondary-main focus:ring-secondary-main"
                : ""
            }`}
          />
        </div>
        {hasError && (
          <p className="text-secondary-main text-body-4-medium mt-1">
            {validationMessage || "This field is required"}
          </p>
        )}
      </div>
    ),

    CHECKBOX: () => (
      <div key={fieldName} className="flex-1 min-w-80 max-w-md mb-4">
        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            id={fieldName}
            checked={formData[fieldName] || false}
            onChange={(e) => handleChange(fieldName, e.target.checked)}
            disabled={disabled}
            className={`w-4 h-4 text-primary-main bg-gray-100 border-gray-300 rounded focus:ring-primary-main focus:ring-2 ${
              hasError ? "border-secondary-main" : ""
            }`}
          />
          {label && (
            <Label
              htmlFor={fieldName}
              className="text-body-3-semibold font-semibold text-black cursor-pointer"
            >
              {label}
              {required && <span className="text-secondary-main ml-1">*</span>}
            </Label>
          )}
        </div>
        {hasError && (
          <p className="text-secondary-main text-body-4-medium mt-1">
            {validationMessage || "This field is required"}
          </p>
        )}
      </div>
    ),

    /** Display-only hint (no input, not persisted). Use `description` or `text` for copy. */
    TEXT_BLOCK: () => (
      <div
        key={fieldName}
        className={cn("flex-1 min-w-80 max-w-2xl mb-4", field.className)}
        role="region"
        aria-label={label || "Information"}
      >
        {label ? (
          <p className="text-xs font-medium text-gray-500 mb-1.5">{label}</p>
        ) : null}
        <p className="text-sm font-semibold text-gray-800 leading-relaxed">
          {field.description || field.text || ""}
        </p>
      </div>
    ),
  };

  const normalizedType =
    filedType === "TEXTAREA"
      ? "TEXT_AREA"
      : filedType === "LOCKED_TEXTAREA"
        ? "LOCKED_TEXT_AREA"
        : filedType;
  const renderFunction =
    fieldTypeMap[normalizedType as keyof typeof fieldTypeMap];

  return renderFunction ? renderFunction() : null;
};
